/* eslint-disable */
import React, { useMemo, useState } from "react";
import {
  Card,
  Group,
  Text,
  Button,
  Grid,
  createStyles,
  Divider,
} from "@mantine/core";
import { IconCalendar } from "@tabler/icons-react";
import { useGetLeaveBalance } from "api/newLeave/getLeavebalance";
import AddLeaveRequestModal from "features/leave-employees/components/addLeave";
import { formatLeaveNumber } from "utils/formatLeaveNumber";

const useStyles = createStyles((theme) => ({
  card: {
    borderRadius: 8,
    height: "100%",
    padding: 14,
    border: `1px solid ${theme.colors.gray[3]}`,
    background: theme.white,
    boxShadow: "0 6px 18px rgba(12,20,40,0.04)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: 800,
    color: theme.colors.dark[8],
  },
  yearBtn: {
    height: 36,
    minWidth: 72,
  },
  content: {
    paddingTop: 6,
    paddingBottom: 6,
  },
  metricsGrid: {
    marginTop: 6,
    marginBottom: 12,
  },
  metricLabel: {
    color: theme.colors.gray[6],
    fontSize: 13,
    marginBottom: 6,
  },
  metricValue: {
    fontSize: 22,
    fontWeight: 800,
    color: theme.colors.dark[8],
    lineHeight: 1,
  },
  applyBtn: {
    marginTop: 10,
    width: "100%",
    background: "#0b1724",
    color: "#fff",
    borderRadius: 8,
    padding: "10px 12px",
    fontWeight: 800,
    "&:hover": {
      background: "#0e2432",
    },
  },
  smallRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "6px 0",
  },
}));

export default function LeaveDetailsList({ year = 2025 }) {
  const { classes } = useStyles();
  const { data, isLoading } = useGetLeaveBalance();
  const [opened, setOpened] = useState(false);

  const computed = useMemo(() => {
    const leaves = data?.data;
    console.log("leaves", leaves);

    if (!leaves) return null;

    const total = Object.entries(leaves).reduce((sum, [key, item]: any) => {
      if (
        item &&
        typeof item === "object" &&
        "balance" in item &&
        key !== "leaveWithoutPay" &&
        ![
          "_id",
          "user",
          "org",
          "year",
          "lastUpdated",
          "createdAt",
          "updatedAt",
          "__v",
        ].includes(key)
      ) {
        return sum + (item.balance ?? 0);
      }
      return sum;
    }, 0);

    const taken = Object.entries(leaves).reduce((sum, [key, item]: any) => {
      if (
        item &&
        typeof item === "object" &&
        "used" in item &&
        key !== "leaveWithoutPay"
      ) {
        return sum + (item.used ?? 0);
      }
      return sum;
    }, 0);

    const pending = Object.entries(leaves).reduce((sum, [key, item]: any) => {
      if (
        item &&
        typeof item === "object" &&
        "pending" in item &&
        key !== "leaveWithoutPay"
      ) {
        return sum + (item.pending ?? 0);
      }
      return sum;
    }, 0);

    const lop = leaves.leaveWithoutPay?.balance ?? 0;
    const lopLabel = lop;

    return {
      total,
      taken,
      absent: 0,
      request: pending,
      workedDays: 0,
      lop,
      lopLabel,
    };
  }, [data]);

  const demo = {
    total: 0,
    taken: 0,
    absent: 0,
    request: 0,
    workedDays: 0,
    lop: 0,
    lopLabel: "N/A",
  };

  const values = computed || demo;

  return (
    <Card className={classes.card} radius="md">
      <div className={classes.header}>
        <Text className={classes.title}>Leave Details</Text>
      </div>

      <Divider />

      <div className={classes.content}>
        <Grid gutter={8} className={classes.metricsGrid}>
          <Grid.Col span={6}>
            <div className={classes.smallRow}>
              <div>
                <Text className={classes.metricLabel}>Total Leaves</Text>
                <Text className={classes.metricValue}>
                  {isLoading ? "--" : formatLeaveNumber(values.total)}
                </Text>
              </div>
            </div>
          </Grid.Col>

          <Grid.Col span={6}>
            <div className={classes.smallRow}>
              <div>
                <Text className={classes.metricLabel}>Taken</Text>
                <Text className={classes.metricValue}>
                  {isLoading ? "--" : formatLeaveNumber(values.taken)}
                </Text>
              </div>
            </div>
          </Grid.Col>

          <Grid.Col span={6}>
            <div className={classes.smallRow}>
              <div>
                <Text className={classes.metricLabel}>Absent</Text>
                <Text className={classes.metricValue}>{formatLeaveNumber(values.absent)}</Text>
              </div>
            </div>
          </Grid.Col>

          <Grid.Col span={6}>
            <div className={classes.smallRow}>
              <div>
                <Text className={classes.metricLabel}>Pending</Text>
                <Text className={classes.metricValue}>{formatLeaveNumber(values.request)}</Text>
              </div>
            </div>
          </Grid.Col>

          <Grid.Col span={6}>
            <div className={classes.smallRow}>
              <div>
                <Text className={classes.metricLabel}>Worked Days</Text>
                <Text className={classes.metricValue}>
                  {isLoading ? "--" : formatLeaveNumber(values.workedDays)}
                </Text>
              </div>
            </div>
          </Grid.Col>

          <Grid.Col span={6}>
            <div className={classes.smallRow}>
              <div>
                <Text className={classes.metricLabel}>Loss of Pay</Text>
                <Text className={classes.metricValue}>
                  {isLoading
                    ? "--"
                    : values.lopLabel === "Loss of Pay"
                    ? values.lopLabel
                    : formatLeaveNumber(values.lop)}
                </Text>
              </div>
            </div>
          </Grid.Col>
        </Grid>

        <Button
          className={classes.applyBtn}
          radius="md"
          onClick={() => setOpened(true)}
        >
          Apply New Leave
        </Button>
      </div>

      <AddLeaveRequestModal opened={opened} onClose={() => setOpened(false)} />
    </Card>
  );
}
